import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import s from './JobsPage.module.css';
import { useJobs } from '@/api/useJobs';
import { Card } from '@/components/primitives/Card';
import { StatusPill } from '@/components/primitives/StatusPill';
import { Spinner } from '@/components/primitives/Spinner';
import { Kebab } from '@/components/primitives/Kebab';
import { Icon } from '@/components/Icon';
import { relativeTime } from '@/lib/relativeTime';
const TABS = ['all', 'running', 'queued', 'failed', 'last_24h'];
const ICON_FOR_TYPE = {
    backup: 'backups',
    restore: 'restore',
    download: 'download',
    purge: 'trash',
    retry_upload: 'refresh',
    meta_self_backup: 'shield',
};
// Human-readable title per job type. Customer backup/restore append the
// database name from the joined row; meta_self_backup has no database_id,
// so the title is self-explanatory.
const TITLE_FOR_TYPE = {
    backup: 'Backup',
    restore: 'Restore',
    meta_self_backup: 'Metadata self-backup',
};
function statusTone(status) {
    if (status === 'running')
        return 'running';
    if (status === 'succeeded')
        return 'ok';
    if (status === 'failed')
        return 'fail';
    if (status === 'retrying')
        return 'retrying';
    return 'queued';
}
function isActive(j) {
    return j.status === 'running' || j.status === 'queued' || j.status === 'retrying';
}
function triggerLabel(j) {
    if (j.trigger === 'schedule')
        return { icon: 'clock', label: 'schedule' };
    if (j.trigger === 'manual')
        return { icon: 'shield', label: 'manual' };
    if (j.trigger === 'auto-retry')
        return { icon: 'refresh', label: 'auto-retry' };
    if (typeof j.trigger === 'object')
        return { icon: 'shield', label: j.trigger.user_email ?? 'user' };
    return { icon: 'clock', label: 'unknown' };
}
export function JobsPage() {
    const [tab, setTab] = useState('all');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const filter = { tab };
    const jobs = useJobs(filter, { live: autoRefresh });
    const items = jobs.data?.items ?? [];
    const active = items.filter(isActive);
    const finished = items.filter((j) => !isActive(j));
    const countByTab = {
        all: items.length,
        running: items.filter((j) => j.status === 'running').length,
        queued: items.filter((j) => j.status === 'queued').length,
        failed: items.filter((j) => j.status === 'failed').length,
        last_24h: items.length,
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: s.header, children: [_jsxs("div", { children: [_jsx("h1", { className: s.h1, children: "Jobs" }), _jsxs("div", { className: s.h1Sub, children: [active.length, " active \u00B7 ", finished.length, " recently finished"] })] }), _jsxs("button", { type: "button", className: s.autoRefresh, "data-on": autoRefresh ? 'true' : undefined, onClick: () => setAutoRefresh((v) => !v), children: [_jsx(Icon, { name: "refresh", size: 12 }), " Auto-refresh: ", autoRefresh ? '5s' : 'off'] })] }), _jsx("div", { className: s.tabs, children: TABS.map((t) => (_jsxs("button", { type: "button", className: `${s.tab} ${tab === t ? s.tabActive : ''}`, onClick: () => setTab(t), children: [_jsx("span", { style: { textTransform: 'capitalize' }, children: t.replace('_', ' ') }), _jsx("span", { className: s.tabCount, children: countByTab[t] })] }, t))) }), active.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: s.sectionHead, children: "Active" }), _jsx(Card, { className: s.list, children: active.map((j) => (_jsx(JobRowView, { j: j, highlight: true }, j.id))) })] })), _jsx("div", { className: s.sectionHead, children: "Recently finished" }), _jsxs(Card, { className: s.list, children: [finished.map((j) => (_jsx(JobRowView, { j: j, highlight: false }, j.id))), finished.length === 0 && (_jsx("div", { style: { padding: 18, color: 'var(--text-tertiary)', fontSize: 13 }, children: "Nothing finished yet." }))] })] }));
}
function JobRowView({ j, highlight }) {
    const tone = statusTone(j.status);
    const trig = triggerLabel(j);
    return (_jsxs("div", { className: `${s.row} ${highlight ? s.highlight : ''}`, children: [_jsx("div", { className: s.tile, children: _jsx(Icon, { name: ICON_FOR_TYPE[j.job_type] ?? 'jobs', size: 16 }) }), _jsxs("div", { children: [_jsxs("div", { className: s.title, children: [TITLE_FOR_TYPE[j.job_type] ?? j.job_type, j.database_name ? ` ${j.database_name}` : ''] }), _jsxs("div", { className: s.sub, children: ["job ", (j.arq_job_id ?? j.id ?? '').slice(0, 8), " \u00B7 attempt ", j.attempt, "/", j.max_attempts] })] }), _jsxs("div", { className: s.statusCol, children: [_jsxs("div", { style: { display: 'flex', gap: 8, alignItems: 'center' }, children: [j.status === 'running' && _jsx(Spinner, { size: 12 }), _jsx(StatusPill, { tone: tone, children: j.status })] }), j.progress_step && _jsx("div", { className: s.meta, children: j.progress_step }), j.status === 'running' && (_jsx("div", { className: s.progressBar, children: _jsx("div", { className: s.progressInner }) }))] }), _jsx("div", { className: s.time, children: j.started_at ? relativeTime(j.started_at) : j.scheduled_at ? relativeTime(j.scheduled_at) : '—' }), _jsxs("div", { className: s.meta, style: { display: 'inline-flex', gap: 6, alignItems: 'center' }, children: [_jsx(Icon, { name: trig.icon, size: 12 }), trig.label] }), _jsx(Kebab, { items: [
                    { label: 'View job JSON', icon: 'info', onClick: () => alert(JSON.stringify(j, null, 2)) },
                ] })] }));
}
